import { useCallback, useRef } from 'react';
import { API_BASE_URL } from '@config';
import { useApi } from '@utils/useApi';
import monitoringService from '@services/monitoring';


/**
 * Custom hook providing memoized functions for interacting with the backend API.
 * Leverages the useApi hook for actual fetch calls with authentication.
 * @returns {object} An object containing various API operation functions.
 */
export const useApiOperations = () => {
  const callApi = useApi();
  const pendingRequests = useRef(new Map());

  /**
   * Internal helper function to make an API request using the useApi hook.
   * Handles monitoring and basic promise structure.
   * @param {string} endpoint - The API endpoint path (relative to API_BASE_URL).
   * @param {object} [options={}] - Fetch options (method, body, headers, etc.).
   * @returns {Promise<any>} A promise resolving with the API response data.
   * @private
   */
  const makeRequest = useCallback(async (endpoint, options = {}) => {
    const startTime = Date.now();
    const requestId = `${endpoint}-${Date.now()}-${Math.random().toString(36).substring(2, 10)}`;

    monitoringService.trackEvent('ApiOperationStarted', {
      requestId,
      endpoint,
      method: options.method || 'GET',
    });


    const promise = callApi(`${API_BASE_URL}${endpoint}`, options)
      .then(response => {
        monitoringService.trackEvent('ApiOperationSucceeded', {
          requestId,
          endpoint,
          duration: Date.now() - startTime
        });
        return response;
      })
      .catch(error => {
        monitoringService.trackException(error, {
          requestId,
          endpoint,
          duration: Date.now() - startTime,
          operation: endpoint.split('/')[2] // Extract operation name from path
        });
        throw error;
      })
      .finally(() => {
        pendingRequests.current.delete(requestId);
      });

    return promise;
  }, [callApi]);

  /**
   * Generates a CV document based on provided data.
   * @param {object} data - The data to include in the CV.
   * @returns {Promise<any>} Promise resolving with the generated CV data (likely blob or similar).
   */
  const downloadCV = useCallback(async (data) => {
    return makeRequest('/api/v1/cv/generate', {
      method: 'POST',
      body: JSON.stringify({ data }),
    });
  }, [makeRequest]);

  /**
   * Uploads a CV file (without triggering extraction).
   * @param {File} file - The CV file to upload.
   * @returns {Promise<any>} Promise resolving with the API response.
   */
  const uploadCV = useCallback(async (file) => {
    const formData = new FormData();
    formData.append('cv', file);

    return makeRequest('/api/v1/cv/upload', {
      method: 'POST',
      headers: {},
      body: formData,
    });
  }, [makeRequest]);

  // /**
  //  * DEPRECATED: Uploads a CV file and triggers the backend extraction process via SSE on the same request.
  //  * Use uploadInitiate and handle SSE separately in the component.
  //  * @deprecated Use uploadInitiate instead.
  //  * @param {File} file - The CV file to upload.
  //  * @param {function} [onProgress] - Optional callback function to receive progress updates { type: string, payload: any }.
  //  * @returns {Promise<object>} Promise resolving with the final extracted data.
  //  */
  // const uploadCVAndExtract = useCallback(async (file, onProgress) => {
  //   const formData = new FormData();
  //   formData.append('cv', file);
  //
  //   return makeRequest('/api/v1/cv/upload', { // Old endpoint
  //     method: 'POST',
  //     body: formData,
  //     onProgress, // This option is no longer directly supported by makeRequest/fetchWithAuth for EventSource
  //   });
  // }, [makeRequest]);

  /**
   * Initiates the CV upload process (Stage 1).
   * Uploads the file and gets back a processing ID and strategy.
   * @param {File} file - The CV file to upload.
   * @returns {Promise<object>} Promise resolving with the initiation response (e.g., { status, processingId, strategy }).
   */
  const uploadInitiate = useCallback(async (file) => {
    const formData = new FormData();
    formData.append('cv', file);

    // Note: No 'onProgress' needed here, this is a standard POST request.
    return makeRequest('/api/v1/cv/initiate', { // New endpoint
      method: 'POST',
      headers: {}, // Let browser set Content-Type for FormData
      body: formData,
    });
  }, [makeRequest]);
  /**
   * Creates a new role based on data potentially extracted from a CV.
   * @param {object} role - The role data to save.
   * @returns {Promise<any>} Promise resolving with the created role data.
   */
  const createRoleFromCV = useCallback(async (role) => {
    return makeRequest('/api/v1/roles', {
      method: 'POST',
      body: JSON.stringify(role),
    });
  }, [makeRequest]);

  /**
   * Fetches the list of skills for the current user.
   * @returns {Promise<Array<object>>} Promise resolving with an array of skill objects.
   */
  const getSkills = useCallback(async () => {
    return makeRequest('/api/v1/skills');
  }, [makeRequest]);

  /**
   * Fetches the list of roles/experiences for the current user.
   * @returns {Promise<Array<object>>} Promise resolving with an array of role objects.
   */
  const getRoles = useCallback(async () => {
    return makeRequest('/api/v1/roles');
  }, [makeRequest]);

  /**
   * Fetches the list of projects for the current user.
   * @returns {Promise<Array<object>>} Promise resolving with an array of project objects.
   */
  const getProjects = useCallback(async () => {
    return makeRequest('/api/v1/projects');
  }, [makeRequest]);

  /**
   * Creates a new role/experience.
   * @param {object} role - The role data to save.
   * @returns {Promise<any>} Promise resolving with the created role data.
   */
  const createRole = useCallback(async (role) => {
     return makeRequest('/api/v1/roles', {
      method: 'POST',
      body: JSON.stringify(role),
    });
  }, [makeRequest]);

  /**
   * Updates an existing role/experience.
   * @param {string} id - The ID of the role to update.
   * @param {object} role - The updated role data.
   * @returns {Promise<any>} Promise resolving with the updated role data.
   */
  const updateRole = useCallback(async (id, role) => {
    return makeRequest(`/api/v1/roles/${id}`, {
      method: 'PUT',
      body: JSON.stringify(role),
    });
  }, [makeRequest]);

  /**
   * Deletes a role/experience.
   * @param {string} id - The ID of the role to delete.
   * @returns {Promise<boolean>} Promise resolving with true on success.
   */
  const deleteRole = useCallback(async (id) => {
    await makeRequest(`/api/v1/roles/${id}`, {
      method: 'DELETE',
    });
    return true;
  }, [makeRequest]);

  /**
   * Creates a new project.
   * @param {object} project - The project data to save.
   * @returns {Promise<any>} Promise resolving with the created project data.
   */
  const createProject = useCallback(async (project) => {
    return makeRequest('/api/v1/projects', {
      method: 'POST',
      body: JSON.stringify(project),
    });
  }, [makeRequest]);

  /**
   * Updates an existing project.
   * @param {string} id - The ID of the project to update.
   * @param {object} project - The updated project data.
   * @returns {Promise<any>} Promise resolving with the updated project data.
   */
  const updateProject = useCallback(async (id, project) => {
    return makeRequest(`/api/v1/projects/${id}`, {
      method: 'PUT',
      body: JSON.stringify(project),
    });
  }, [makeRequest]);

  /**
   * Deletes a project.
   * @param {string} id - The ID of the project to delete.
   * @returns {Promise<boolean>} Promise resolving with true on success.
   */
  const deleteProject = useCallback(async (id) => {
    await makeRequest(`/api/v1/projects/${id}`, {
      method: 'DELETE',
    });
    return true;
  }, [makeRequest]);

  /**
   * Creates a new skill.
   * @param {object} skill - The skill data to save.
   * @returns {Promise<any>} Promise resolving with the created skill data.
   */
  const createSkill = useCallback(async (skill) => {
    return makeRequest('/api/v1/skills', {
      method: 'POST',
      body: JSON.stringify(skill),
    });
  }, [makeRequest]);

  /**
   * Updates an existing skill.
   * @param {string} id - The ID of the skill to update.
   * @param {object} skill - The updated skill data.
   * @returns {Promise<any>} Promise resolving with the updated skill data.
   */
  const updateSkill = useCallback(async (id, skill) => {
    return makeRequest(`/api/v1/skills/${id}`, {
      method: 'PUT',
      body: JSON.stringify(skill),
    });
  }, [makeRequest]);

  /**
   * Deletes a skill.
   * @param {string} id - The ID of the skill to delete.
   * @returns {Promise<boolean>} Promise resolving with true on success.
   */
  const deleteSkill = useCallback(async (id) => {
    await makeRequest(`/api/v1/skills/${id}`, {
      method: 'DELETE',
    });
    return true;
  }, [makeRequest]);

  /**
   * Creates a new job application record.
   * @param {object} application - The job application data to save.
   * @returns {Promise<any>} Promise resolving with the created job application data.
   */
  const createJobApplication = useCallback(async (application) => {
    return makeRequest('/api/v1/job-applications', {
      method: 'POST',
      body: JSON.stringify(application),
    });
  }, [makeRequest]);

  /**
   * Fetches the list of job applications for the current user.
   * @returns {Promise<Array<object>>} Promise resolving with an array of job application objects.
   */
  const getJobApplications = useCallback(async () => {
    return makeRequest('/api/v1/job-applications');
  }, [makeRequest]);

  /**
   * Updates an existing job application record.
   * @param {string} id - The ID of the job application to update.
   * @param {object} data - The updated job application data.
   * @returns {Promise<any>} Promise resolving with the updated job application data.
   */
  const updateJobApplication = useCallback(async (id, data) => {
     return makeRequest(`/api/v1/job-applications/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }, [makeRequest]);

  /**
   * Fetches statistics about the user's job applications.
   * @returns {Promise<object>} Promise resolving with job application statistics.
   */
  const getJobApplicationStats = useCallback(async () => {
    return makeRequest('/api/v1/job-applications/stats');
  }, [makeRequest]);

  /**
   * Deletes a job application record.
   * @param {string} id - The ID of the job application to delete.
   * @returns {Promise<boolean>} Promise resolving with true on success.
   */
  const deleteJobApplication = useCallback(async (id) => {
    await makeRequest(`/api/v1/job-applications/${id}`, {
      method: 'DELETE',
    });
    return true;
  }, [makeRequest]);

  /**
   * Uploads a job specification file for a specific job application.
   * @param {string} id - The ID of the job application.
   * @param {File} file - The job specification file to upload.
   * @returns {Promise<any>} Promise resolving with the API response.
   */
  const uploadJobSpec = useCallback(async (id, file) => {
    const formData = new FormData();
    formData.append('spec', file);

    return makeRequest(`/api/v1/job-applications/${id}/spec`, {
      method: 'POST',
      headers: {},
      body: formData,
    });
  }, [makeRequest]);

  /**
   * Sends a message to the chat API endpoint.
   * @param {string} message - The user's chat message.
   * @returns {Promise<object>} Promise resolving with the assistant's response.
   */
  const sendChatMessage = useCallback(async (message) => {
    return makeRequest('/api/v1/chat', {
      method: 'POST',
      body: JSON.stringify({ message }),
    });
  }, [makeRequest]);

  /**
   * Fetches the profile completion status for the current user.
   * Handles auth errors specifically to return { complete: false }.
   * @returns {Promise<object>} Promise resolving with { complete: boolean }.
   */
  const getProfileStatus = useCallback(async () => {
    try {
      return await makeRequest('/api/v1/profile/status');
    } catch (error) {
      monitoringService.trackEvent('ProfileStatusError', {
        errorType: error.type,
        isAuthError: error.type === 'auth_required' || error.type === 'auth_failed'
      });

      if (error.type === 'auth_required' || error.type === 'auth_failed') {
        return { complete: false };
      }
      throw error;
    }
  }, [makeRequest]);

  /**
   * Fetches the full profile data for the current user.
   * @returns {Promise<object>} Promise resolving with the user's profile data.
   */
  const getProfile = useCallback(async () => {
    return makeRequest('/api/v1/profile');
  }, [makeRequest]);

  /**
   * Updates the user's profile data.
   * @param {object} profileData - The profile data to save.
   * @returns {Promise<any>} Promise resolving with the updated profile data.
   */
  const updateProfile = useCallback(async (profileData) => {
    return makeRequest('/api/v1/profile', {
      method: 'PUT',
      body: JSON.stringify(profileData),
    });
  }, [makeRequest]);

  /**
   * Saves profile data extracted from a CV import.
   * Calls the specific backend endpoint for handling CV import data.
   * @param {object} profileData - The extracted profile data ({ personalStatement, education, roles, globalSkills }).
   * @returns {Promise<any>} Promise resolving with the API response.
   */
  const saveImportedProfile = useCallback(async (profileData) => {
    return makeRequest('/api/v1/profile/import-cv', {
      method: 'POST',
      body: JSON.stringify(profileData),
    });
  }, [makeRequest]);
 
  return {
    downloadCV,
    getProfile,
    updateProfile,
    uploadCV,
    // uploadCVAndExtract, // Deprecated
    uploadInitiate, // New function for Stage 1
    createRoleFromCV,
    getSkills,
    getRoles,
    getProjects,
    createRole,
    updateRole,
    deleteRole,
    createProject,
    updateProject,
    deleteProject,
    createSkill,
    updateSkill,
    deleteSkill,
    createJobApplication,
    getJobApplications,
    updateJobApplication,
    getJobApplicationStats,
    deleteJobApplication,
    uploadJobSpec,
    sendChatMessage,
    getProfileStatus,
    saveImportedProfile,
  };
};
